"use client";

import { useEffect, useState, useRef } from "react";
import { useParams } from "next/navigation";
import HeaderInner from "@/components/HeaderInner";
import Image from "next/image";
import * as ReactDOM from "react-dom";
import { getUserInfo } from "../../../../utilities/userInfo";
import FilterBar from "@/app/cin_booking_engine/Filterbar";
import { BookingEngineProvider } from "@/app/cin_context/BookingEngineContext";
import GalleryModal from "@/components/GalleryModal";
import TigerLoader from "@/components/TigerLoader";
import { AMENITY_ICON_MAP } from "../../../../utilities/amenitiesMap";

export default function RoomsPage() {
  const [roomsData, setRoomsData] = useState([]);
  const [propertyData, setPropertyData] = useState([]);
  const [propertyId, setPropertyId] = useState(null);
  const { propertySlug } = useParams();
  const [loading, setLoading] = useState(false);
  const [brandId, setBrandId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isOpen, setOpen] = useState(false);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const filterBarRef = useRef(null);
  const [staahPropertyId, setStaahPropertyId] = useState(null);
  const [cityDetails, setCityDetails] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [brandData, setBrandData] = useState([]);
  const [roomsBannerData, setRoomsBannerData] = useState([]);
  const [isHandleSearchProp, setHandleSearchProp] = useState(false);
  const [isBannerExpanded, setIsBannerExpanded] = useState(false);

  const brandColors = {
    1: "#bf1e2d",
    4: "#bf1e2d",
    2: "#263997",
    3: "#f04e02",
    5: "#febb19",
    6: "#f9e12e",
  };

  const backgroundColor = brandColors[brandId] || "#f26624";
  const DUMMY_AMENITY_ICON = "/dummyAmmenitiesIcon.webp";

  const getAmenityIcon = (amenityName) => {
    if (!amenityName || typeof amenityName !== "string") return "";

    // Direct exact match first
    if (AMENITY_ICON_MAP[amenityName]) return AMENITY_ICON_MAP[amenityName];

    // Case-insensitive match
    const normalized = amenityName.trim().toLowerCase();
    const matchingKey = Object.keys(AMENITY_ICON_MAP).find(
      (key) => key.toLowerCase() === normalized
    );

    return matchingKey ? AMENITY_ICON_MAP[matchingKey] : "";
  };

  const stripHtml = (html) => html?.replace(/<[^>]+>/g, "") || "";

  useEffect(() => {
    const storedBrandId = localStorage.getItem("brandId");
    typeof window !== "undefined" && window.scrollTo(0, 0);
    setBrandId(storedBrandId);
  }, []);

  // Fetch brand data
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/property/GetBrandList`
        );
        const json = await res.json();
        setBrandData(json.data || []);
      } catch (e) {
        console.error("Error fetching brands:", e);
      }
    })();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const storedBrandId = localStorage.getItem("brandId");
    setBrandId(storedBrandId);
  }, []);

  useEffect(() => {
    if (!propertySlug) return;

    const fetchPropertyIdFromSlug = async (slug) => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/property/GetPropertyList`
        );
        const json = await res?.json();
        if (json.errorMessage !== "success") {
          console.error("Property list fetch error:", json);
          return null;
        }
        const found = json.data.find((p) => p.propertySlug === propertySlug);
        setPropertyData(found);
        setCityDetails({
          label: found?.cityName,
          value: found?.cityId,
          property_Id: found?.staahPropertyId,
        });
        setStaahPropertyId(found?.staahPropertyId);
        return found?.propertyId || null;
      } catch (error) {
        console.error("Error fetching property list:", error);
        return null;
      }
    };

    const loadPropertyId = async () => {
      setLoading(true);
      const id = await fetchPropertyIdFromSlug(propertySlug);
      setPropertyId(id);
      setLoading(false);
    };

    loadPropertyId();
  }, [propertySlug]);

  function formatDate(date) {
    return date.toISOString().split("T")[0];
  }

  function formatCmsRooms(cmsRooms = []) {
    return cmsRooms
      .filter((r) => r.enabled === "E")
      .map((room) => ({
        roomName: room.roomName,
        roomDesc: room.roomDesc || "",

        roomImages: (room.roomImages || []).map((img) => ({
          roomImage: img.roomImage,
        })),

        // CMS → flat amenities
        roomAmenities: (room.roomServices || []).map((service) => ({
          name: service.serviceName,
          icon: service.iconImage || DUMMY_AMENITY_ICON,
        })),
      }));
  }

  function formatStaahRooms(staahResponse = []) {
    if (!Array.isArray(staahResponse) || !staahResponse[0]) return [];

    const roomData = staahResponse[0]?.RoomData || [];

    return roomData.map((room) => {
      const amenities = [];

      // STAAH → keep category + use AMENITY_ICON_MAP
      Object.entries(room.RoomAmenities || {}).forEach(([category, items]) => {
        items.forEach((item) => {
          amenities.push({
            name: item,
            category,
            icon: getAmenityIcon(item) || DUMMY_AMENITY_ICON, // ✅ Uses your map first
          });
        });
      });

      return {
        roomName: room.RoomDisplayName || room.RoomName,
        roomDesc: room.RoomDescription || "",
       roomRate: parseFloat(room.RackRate) || 0,
        roomImages: (room.Images || []).map((img) => ({
          roomImage: img,
        })),
        roomAmenities: amenities,
      };
    });
  }

  const fetchRoomsBanner = async () => {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/property/GetPropertyRoomBanner?propertyId=${propertyId}`
      );
      const json = await res.json();
      if (
        json?.errorCode === "0" &&
        Array.isArray(json.data) &&
        json.data.length > 0
      ) {
        setRoomsBannerData(json.data?.[0]);
      } else {
        console.warn("No data found in API");
        setRoomsBannerData([]);
      }
    } catch (err) {
      console.error("Error fetching dining details:", err);
      setRoomsBannerData([]);
    }
  };
  

 useEffect(() => {
  if (!propertyId) return;

  const showRoomFrom = propertyData?.showRoomFrom;
  const staahPropertyId = propertyData?.staahPropertyId;

  const fetchRoomsDetails = async () => {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/rooms/GetRoomsByProperty?PropertyId=${propertyId}`
      );
      const json = await res.json();

      if (json.errorMessage !== "success") {
        console.error("Property details fetch error:", json);
        return;
      }

      const fetchedPropertyData = json.data?.[0]?.roomsInfo || [];
      const formattedCmsRooms = formatCmsRooms(fetchedPropertyData);
      setRoomsData(formattedCmsRooms);
    } catch (error) {
      console.error("Error fetching property details:", error);
    } finally {
      setIsPageLoading(false);
    }
  };

  const fetchRoomsFromStaah = async () => {
    const today = new Date();
    const checkInDate = formatDate(today);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const checkOutDate = formatDate(tomorrow);

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/StaahData/rates/GetRoomsRates?RequestType=bedata&PropertyId=${staahPropertyId}&Product=yes&CheckInDate=${checkInDate}&CheckOutDate=${checkOutDate}&PromoCode=""`
      );

      const json = await res.json();

      if (json.ErrorCode !== "0") {
        console.error("STAAH fetch error:", json);
        return;
      }

      const formattedRooms = formatStaahRooms(json?.PropertyList);

      const sortedByLowestPrice = [...formattedRooms].sort(
        (a, b) => a.roomRate - b.roomRate
      );

      setRoomsData(sortedByLowestPrice);
    } catch (error) {
      console.error("Error fetching STAAH rooms:", error);
    } finally {
      setIsPageLoading(false);
    }
  };

  if (showRoomFrom === "StaahRoomData") {
    fetchRoomsFromStaah();
  } else {
    fetchRoomsDetails();
  }

  fetchRoomsBanner();

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [propertyId, propertyData?.showRoomFrom, propertyData?.staahPropertyId]);

  const handlePropertyBookNow = async () => {
    setOpen(!isOpen);
    setShowFilterBar(!showFilterBar);
    setHandleSearchProp(true);
    if (filterBarRef.current) {
      filterBarRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      const firstInput = filterBarRef.current.querySelector(
        "input, select, button"
      );
      if (firstInput) firstInput.focus();
    }
  };

  async function postBookingWidged(
    rooms,
    mapping,
    isClose,
    ctaName,
    selectedPropertyId
  ) {
    const resp = await getUserInfo();
    const sessionId = sessionStorage?.getItem("sessionId");
    const payload = {
      ctaName: ctaName,
      urls: window.location.href,
      cityId: "0",
      propertyId: selectedPropertyId?.toString() || "0",
      checkIn: "",
      checkOut: "",
      adults: "0",
      children: "0",
      rooms: "0",
      promoCode: "",
      ip: resp?.ip,
      sessionId: sessionId,
      deviceName: resp?.deviceInfo?.deviceName,
      deviceType:
        resp?.deviceInfo?.deviceOS == "Unknown"
          ? resp?.deviceInfo?.platform
          : resp?.deviceInfo?.deviceOS,
      roomsName: rooms?.RoomName,
      packageName: mapping?.MappingName,
      isCartOpen: mapping?.MappingName ? "Y" : "N",
      isCartEdit: "N",
      isCartClick: "N",
      isClose: isClose ? "Y" : "N",
    };
       const response = await fetch(
         `${process.env.NEXT_PUBLIC_CMS_BASE_URL}/Api/tracker/BookingWidged`,
         {
           method: "POST",
           headers: {
             "Content-Type": "application/json",
           },
           body: JSON.stringify( payload ),
         }
       );
       const res = await response?.json();
  }

  if (isPageLoading) {
    return <TigerLoader />;
  }

  return (
    <main className="bg-white" style={{ overflowY: "auto" }}>
      <HeaderInner
        brandData={brandData}
        propertyData={propertyData}
        brandId={brandId}
        backgroundColor={backgroundColor}
        propertyId={propertyData?.propertyId}
        onClick={handlePropertyBookNow}
      />
      <section ref={filterBarRef} className="position-relative">
        <section
          className="position-fixed start-0 w-100 bg-white shadow"
          ref={filterBarRef}
        >
          {showFilterBar && typeof window !== "undefined" &&
            ReactDOM.createPortal(
              <section className="filter-bar-hotels-cin">
                <BookingEngineProvider>
                  <FilterBar
                    selectedProperty={parseInt(cityDetails.property_Id)}
                    cityDetails={cityDetails}
                    openBookingBar={showFilterBar}
                    onClose={(isReload) => {
                      setOpen(!isOpen);
                      setShowFilterBar(!showFilterBar);
                      if (isReload) {
                        window.location.reload();
                      }
                    }}
                    isCloseButtonShow="true"
                    backgroundColor={backgroundColor}
                    isHandleSearchProp={isHandleSearchProp}
                  />
                </BookingEngineProvider>
              </section>,
              document.body
            )}
        </section>
      </section>
      {/* Top Banner */}
      <div style={{ paddingTop: "80px" }}></div>
      {/* Intro */}
      <section className="text-center py-12 px-4">
        <h1
          className="text-orange fw-semibold mb-4 custom-hotel-header mb-3"
          style={{ color: backgroundColor }}
        >
          {roomsBannerData?.roomBannerTitle}
        </h1>
        <div
          className="w-10 h-1 bg-orange-500 mx-auto mt-2 mb-4"
          style={{ background: backgroundColor }}
        ></div>
        <p className="max-w-3xl mx-auto text-gray-600 mb-6 !text-[14px]">
          {isBannerExpanded ||
          stripHtml(roomsBannerData.roomBannerDesc).length <=
            (isMobile ? 100 : 200) ? (
            <span
              dangerouslySetInnerHTML={{
                __html: roomsBannerData.roomBannerDesc,
              }}
            />
          ) : (
            <>
              {stripHtml(roomsBannerData.roomBannerDesc).slice(
                0,
                isMobile ? 100 : 200,
              )}
              ...
            </>
          )}

          {stripHtml(roomsBannerData.roomBannerDesc).length >
            (isMobile ? 100 : 200) && (
            <button
              onClick={() => setIsBannerExpanded(!isBannerExpanded)}
              className="text-orange-600 font-semibold underline ml-2 !text-[14px]"
              style={{ color: backgroundColor }}
            >
              {isBannerExpanded ? "Read Less" : "Read More"}
            </button>
          )}
        </p>
      </section>

      {/* Rooms */}
      <div className="space-y-8 container">
        {roomsData?.map((room, idx) => (
          <div
            key={idx}
            className={`relative flex flex-col md:flex-row ${
              idx % 2 !== 0 ? "md:flex-row-reverse" : ""
            } md:items-center md:gap-12`}
            style={{ marginBottom: "70px" }}
          >
            {/* Image */}
            <div className="md:w-1/2">
              <Image
                src={room?.roomImages?.[0]?.roomImage || "/no_image.jpg"}
                alt={room?.roomName || "Room Image"}
                className="w-full rounded-0 shadow-lg h-[200px] md:h-[380px]"
                height={400}
                width={400}
              />
            </div>

            {/* Text Card */}

            <div className="md:w-1/2 relative z-10 md:-ml-20 md:-mr-20">
              <div className="absolute -left-4 w-full h-full bg-white trans-layer -z-10"></div>

              <div className="bg-white shadow-lg p-8">
                <h3
                  className="custom-hotel-dineHeader font-bold mb-4"
                  style={{ color: backgroundColor }}
                >
                  {room?.roomName}
                </h3>
                <p className="text-sm font-semibold mb-2 d-none">
                  ₹ {room?.roomRate?.toLocaleString("en-IN")} / night
                </p>
                <p className="text-gray-600 mb-6 !text-[14px]">
                  {room.roomDesc?.length > (isMobile ? 200 : 220)
                    ? `${room.roomDesc.slice(0, isMobile ? 200 : 220)}...`
                    : room.roomDesc}

                  {room.roomDesc?.length > (isMobile ? 200 : 220) && (
                    <button
                      onClick={() => {
                        setSelectedRoom(room);
                        setShowModal(true);
                      }}
                      className="text-orange-600 font-semibold underline ml-2 !text-[14px]"
                      style={{ color: backgroundColor }}
                    >
                      Read More
                    </button>
                  )}
                </p>
                <h4
                  className="font-semibold mt-4 mb-2"
                  style={{ color: backgroundColor, fontSize: "14px" }}
                >
                  AMENITIES
                </h4>
                {room?.roomAmenities?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-y-2 gap-x-3 text-gray-600 text-sm mb-4">
                    {room.roomAmenities.map((amenity, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Image
                          src={amenity.icon}
                          alt={amenity.name}
                          width={16}
                          height={16}
                          className="me-2"
                        />
                        {/* <span><FontAwesomeIcon className="me-1" icon={faDiamond} size="xs" color="grey"></FontAwesomeIcon>{"  "}{amenity.name}</span> */}
                        <span>{amenity.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm mb-4">
                    No amenities available.
                  </p>
                )}

                <div className="flex gap-3">
                  <button
                    className="px-2 py-1 !text-[12px]"
                    style={{
                      border: `1px solid ${backgroundColor}`,
                      color: "black",
                    }}
                    onClick={() => {
                      setSelectedRoom(room);
                      setShowModal(true);
                    }}
                  >
                    Explore more
                  </button>

                  <button
                    className="px-3 py-1 text-white !text-[10px]"
                    style={{ background: backgroundColor }}
                    onClick={handlePropertyBookNow}
                  >
                    Book Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <GalleryModal
        backgroundColor={backgroundColor}
        showModal={showModal}
        setShowModal={setShowModal}
        roomData={selectedRoom}
      />
    </main>
  );
}
